import { useEffect, useState, useMemo } from 'react';
import { RunContextApp } from 'framework/RCApp';
import { getFuelTypeFromTemplateKey } from 'apis/dataScenariosAPI';
import { fetchProjectAttributes, type ProjectAttributesForFuel } from 'utils/template-list';
import { mapMeasurementTypeToFuelType, extractProjectFuelType, getAvailableFuelTypes } from 'features/contentManagement/dataScenarios/dataScenariosUtils';

interface ProjectAttributes {
  fuel_type?: string | null;
}

export const useProjectFuelType = (
  rc: RunContextApp,
  pilotId: string | null | undefined,
  projectAttributes: ProjectAttributes | null | undefined,
  shouldFetch: boolean = true
) => {
  const [fetchedProjectAttributes, setFetchedProjectAttributes] = useState<ProjectAttributesForFuel | null>(null);

  useEffect(() => {
    if (!shouldFetch || !pilotId) {
      setFetchedProjectAttributes(null);
      return;
    }

    if (projectAttributes?.fuel_type) {
      setFetchedProjectAttributes(null);
      return;
    }

    let ignore = false;

    fetchProjectAttributes(rc, String(pilotId))
      .then((attrs: ProjectAttributesForFuel | null) => {
        if (!ignore && attrs) {
          setFetchedProjectAttributes(attrs);
        }
      })
      .catch((err: any) => {
        if (!ignore) {
          console.error('[useProjectFuelType] Failed to fetch project attributes:', err);
        }
      });
    return () => { ignore = true; };
  }, [pilotId, projectAttributes?.fuel_type, rc, shouldFetch]);

  const projectFuelType = useMemo(() => {
    return extractProjectFuelType(projectAttributes, fetchedProjectAttributes);
  }, [projectAttributes, fetchedProjectAttributes]);

  const projectFuelTypes = useMemo(() => {
    const fuelTypeValue =
      projectAttributes?.fuel_type ?? fetchedProjectAttributes?.allowed_measurement_type ?? '';
    return getAvailableFuelTypes(typeof fuelTypeValue === 'string' ? fuelTypeValue : String(fuelTypeValue));
  }, [projectAttributes?.fuel_type, fetchedProjectAttributes?.allowed_measurement_type]);

  return {
    projectFuelType,
    projectFuelTypes,
    fetchedProjectAttributes
  };
};

export const useAvailableFuelTypes = (
  rc: RunContextApp,
  pilotId: string | null | undefined,
  enabled: boolean = true
) => {
  const [availableFuelTypes, setAvailableFuelTypes] = useState<string[]>(['ELECTRIC']);
  const [projectAttributes, setProjectAttributes] = useState<ProjectAttributesForFuel | null>(null);

  useEffect(() => {
    if (!enabled || !pilotId) {
      setAvailableFuelTypes(['ELECTRIC']);
      setProjectAttributes(null);
      return;
    }

    let ignore = false;

    fetchProjectAttributes(rc, String(pilotId))
      .then((attrs: ProjectAttributesForFuel | null) => {
        if (!ignore && attrs) {
          setProjectAttributes(attrs);
          const fuelTypes = getAvailableFuelTypes(attrs.allowed_measurement_type);
          setAvailableFuelTypes(fuelTypes);
        }
      })
      .catch((err: any) => {
        if (!ignore) {
          console.error('[useAvailableFuelTypes] Failed to fetch project attributes:', err);
          setAvailableFuelTypes(['ELECTRIC']);
        }
      });
    return () => { ignore = true; };
  }, [pilotId, rc, enabled]);

  return {
    availableFuelTypes,
    projectAttributes
  };
};

export const getDefaultFuelType = (
  templateKey: string | undefined,
  projectAttributes: ProjectAttributesForFuel | null,
  availableFuelTypes: string[]
): string => {
  if (templateKey) {
    const templateFuelType = getFuelTypeFromTemplateKey(templateKey);
    if (templateFuelType) {
      const mapped = mapMeasurementTypeToFuelType(templateFuelType);
      if (mapped && availableFuelTypes.includes(mapped)) {
        return mapped;
      }
    }
  }

  if (projectAttributes?.allowed_measurement_type) {
    const fuelTypes = projectAttributes.allowed_measurement_type
      .split(',')
      .map((f: string) => f.trim())
      .map(mapMeasurementTypeToFuelType)
      .filter((f: string | undefined): f is string => f !== undefined);

    if (fuelTypes.length > 0 && availableFuelTypes.includes(fuelTypes[0])) {
      return fuelTypes[0];
    }
  }

  return availableFuelTypes.length > 0 ? availableFuelTypes[0] : 'ELECTRIC';
};
